import { ProgressCircle } from '@lego/connect-components-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChatMessage } from '../../hooks/use-assistant-chat';
import { cn } from '../../utils/cn';

interface MessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
}

export const MessageList = ({ messages, isLoading }: MessageListProps) => {
  const { t } = useTranslation();
  const endRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    if (messages.length === 0 && !isLoading) return;
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, isLoading]);

  if (messages.length === 0 && !isLoading) {
    return <p className="text-body-md text-core-slate-700">{t('assistant.display.emptyState')}</p>;
  }

  return (
    // role="log" + aria-live so screen readers announce new answers as they arrive.
    <div role="log" aria-live="polite" className="flex flex-col gap-200">
      {messages.map((message, index) => (
        <div
          // Messages are append-only and never reordered, so the index is a stable identity.
          // biome-ignore lint/suspicious/noArrayIndexKey: append-only list, order never changes
          key={index}
          className={cn(
            'max-w-[85%] rounded-100 px-200 py-100 text-body-md whitespace-pre-wrap break-words',
            message.role === 'user'
              ? 'self-end bg-core-blue-900 text-core-white'
              : 'self-start bg-core-slate-100 text-core-slate-1300'
          )}
        >
          <span className="sr-only">
            {message.role === 'user'
              ? t('assistant.display.userLabel')
              : t('assistant.display.assistantLabel')}
            {': '}
          </span>
          {message.content}
        </div>
      ))}
      {isLoading && (
        <div className="flex items-center gap-100 self-start text-body-md text-core-slate-700">
          <ProgressCircle indeterminate size="small" aria-label={t('assistant.display.pending')} />
          {t('assistant.display.pending')}
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
};
